import { clientProductCode } from "../config/config";
import { functions } from '../firebase/Firebase';
import CharacterSerializer from '../models/characters/CharacterSerializer';
import CharacterIdentificationConditionsGroup from '../models/identification_conditions_groups/CharacterIdentificationConditionsGroup';
import WriteDataMode from "./WriteDataMode";

export const CharacterActionTypes = {
    GetCharacters: 'GetCharacters',
    GetCharactersDone: 'GetCharactersDone',
    GetCharacter: 'GetCharacter',
    GetCharacterDone: 'GetCharacterDone',
    WriteCharacter: 'WriteCharacter',
    WriteCharacterDone: 'WriteCharacterDone',
    VerifyCharacter: 'VerifyCharacter',
    VerifyCharacterDone: 'VerifyCharacterDone',
    RefreshCharacterGameData: 'RefreshCharacterGameData',
    RefreshCharacterGameDataDone: 'RefreshCharacterGameDataDone',
    IdentifyCharacter: 'IdentifyCharacter',
    IdentifyCharacterDone: 'IdentifyCharacterDone',
};

function getCharactersFunction() {
    return functions.httpsCallable('neofelisGetCharacters');
}

export const getCharacters = (lastCharacterId) => {
    return async (dispatch, getState) => {
        try {
            const characterState = getState().characterState;
            const getCharactersCompletedInState = characterState.getCharactersCompleted;
            if (getCharactersCompletedInState === false) {
                return;
            }

            dispatch({
                type: CharacterActionTypes.GetCharacters,
            });

            const callable = getCharactersFunction();
            const data = {
                clientProductCode: clientProductCode,
                lastCharacterId: lastCharacterId,
            };

            const result = await callable(data);

            const jsonCharacters = result.data.characters;
            const characters = [];
            for (let i = 0; i < jsonCharacters.length; i++) {
                const jsonCharacter = jsonCharacters[i];
                const character = CharacterSerializer.deserialize(jsonCharacter);
                characters.push(character);
            }

            dispatch({
                type: CharacterActionTypes.GetCharactersDone,
                characters: characters,
            });
        } catch (error) {
            dispatch({
                type: CharacterActionTypes.GetCharactersDone,
                error: error,
            });
        }
    };
};

function getCharacterFunction() {
    return functions.httpsCallable('neofelisGetCharacter');
}

export const getCharacter = (characterId) => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: CharacterActionTypes.GetCharacter,
            });

            const callable = getCharacterFunction();
            const data = {
                clientProductCode: clientProductCode,
                characterId: characterId,
            };

            const result = await callable(data);
            const character = (result.data.character !== null) ? CharacterSerializer.deserialize(result.data.character) : null;

            dispatch({
                type: CharacterActionTypes.GetCharacterDone,
                characterId: characterId,
                character: character,
            });
        } catch (error) {
            dispatch({
                type: CharacterActionTypes.GetCharacterDone,
                error: error,
            });
        }
    };
};

function writeCharacterFunction() {
    return functions.httpsCallable('neofelisWriteCharacter');
}

const writeCharacter = (mode, character) => {
    return async (dispatch, getState) => {
        try {
            const characterState = getState().characterState;
            const writeCharacterCompletedInState = characterState.writeCharacterCompleted;
            if (writeCharacterCompletedInState === false) {
                return;
            }

            dispatch({
                type: CharacterActionTypes.WriteCharacter,
            });

            const callable = writeCharacterFunction();
            const data = {
                clientProductCode: clientProductCode,
                mode: mode.wireName,
                character: CharacterSerializer.serialize(character),
            };

            const result = await callable(data);
            character.id = result.data.characterId;

            if (result.data.writtenData !== null) {
                character.merge(result.data.writtenData);
            }

            dispatch({
                type: CharacterActionTypes.WriteCharacterDone,
                message: result.data.message,
                docId: result.data.docId,
                mode: mode,
                character: character,
            });
        } catch (error) {
            dispatch({
                type: CharacterActionTypes.WriteCharacterDone,
                error: error,
            });
        }
    };
};

export const addCharacter = (character) => {
    return writeCharacter(WriteDataMode.add, character);
};

export const updateCharacter = (character) => {
    return writeCharacter(WriteDataMode.update, character);
};

export const deleteCharacter = (character) => {
    return writeCharacter(WriteDataMode.delete, character);
};

function verifyCharacterFunction() {
    return functions.httpsCallable('neofelisVerifyCharacter');
}

export const verifyCharacter = (character) => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: CharacterActionTypes.VerifyCharacter,
            });

            const callable = verifyCharacterFunction();
            const data = {
                clientProductCode: clientProductCode,
                characterId: character.id,
            };

            const result = await callable(data);

            if (result.data.writtenData !== null) {
                character.merge(result.data.writtenData);
            }

            dispatch({
                type: CharacterActionTypes.VerifyCharacterDone,
                message: result.data.message,
                character: character,
            });
        } catch (error) {
            dispatch({
                type: CharacterActionTypes.VerifyCharacterDone,
                error: error,
            });
        }
    };
};

function refreshCharacterGameDataFunction() {
    return functions.httpsCallable('neofelisRefreshCharacterGameData');
}

export const refreshCharacterGameData = (character) => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: CharacterActionTypes.RefreshCharacterGameData,
            });

            const callable = refreshCharacterGameDataFunction();
            const data = {
                clientProductCode: clientProductCode,
                characterId: character.id,
            };

            const result = await callable(data);

            if (result.data.writtenData !== null) {
                character.merge(result.data.writtenData);
            }

            dispatch({
                type: CharacterActionTypes.RefreshCharacterGameDataDone,
                message: result.data.message,
                character: character,
            });
        } catch (error) {
            dispatch({
                type: CharacterActionTypes.RefreshCharacterGameDataDone,
                error: error,
            });
        }
    };
};

function identifyCharacterFunction() {
    return functions.httpsCallable('neofelisIdentifyCharacter');
}

export const identifyCharacter = (characterIdentificationConditionsGroup) => {
    return async (dispatch, getState) => {
        try {
            dispatch({
                type: CharacterActionTypes.IdentifyCharacter,
            });

            const callable = identifyCharacterFunction();
            const data = {
                clientProductCode: clientProductCode,
                characterIdentificationConditionsGroup: CharacterIdentificationConditionsGroup.serialize(characterIdentificationConditionsGroup),
            };

            const result = await callable(data);

            const jsonCharacters = result.data.characters;
            const characters = [];

            for (let i = 0; i < jsonCharacters.length; i++) {
                const jsonCharacter = jsonCharacters[i];
                const character = CharacterSerializer.deserialize(jsonCharacter);
                characters.push(character);
            }

            dispatch({
                type: CharacterActionTypes.IdentifyCharacterDone,
                characters: characters,
            });
        } catch (error) {
            dispatch({
                type: CharacterActionTypes.IdentifyCharacterDone,
                error: error,
            });
        }
    };
};
